<template>
  <div class="card p-3">
    <v-expansion-panels
        v-model="activeSteps"
        ref="panels"
        multiple
        flat
        mandatory
    >
      <slot name="steps-before"></slot>
      <v-expansion-panel
          v-for="(stepSchema, stepKey, stepIndex) in steps"
          v-show="stepSchema.show"
          v-bind:disabled="flowControl && disabledStep(stepSchema.index)"
          :key="refTemplate(stepKey)"
      >
        <v-divider
            class="mt-3 mb-4"
            v-if="showStepHeaderDividers"
        ></v-divider>
        <v-expansion-panel-header>
          <h2>
            <span class="expansion-panel-header-number">
              {{ getStepHeaderNumber(stepKey) }}<span v-if="schema.stepNumbersWithDots">.</span>
            </span>
            <span class="expansion-panel-header-title">
              {{ stepSchema.title }}
            </span>
            <v-btn
                class="ml-5"
                v-if="stepSchema.addRemoveStepButton"
                @click.stop="removeStep(stepKey)"
                color="#fafafa"
                depressed
            >
              <v-icon class="mr-2">mdi-delete-forever-outline</v-icon>
              <span class="text-decoration-underline">
                {{ stepSchema.removeStepButtonText }}
              </span>
            </v-btn>
          </h2>
        </v-expansion-panel-header>
        <v-expansion-panel-content eager>
          <v-divider
              class="mt-4 mb-6"
              v-if="showStepHeaderDividers"
          ></v-divider>
          <p
              class="step-description"
              v-if="stepSchema.description"
          >
            {{ stepSchema.description }}
          </p>
          <flow-step
              :flatten-model="flattenModel"
              :ref="refTemplate(stepKey)"
              :schema="stepSchema"
              :stepName="stepKey"
              :flowName="schema.name"
              :auto-continue="autoContinue"
              v-model="model[stepKey]"
              @nextStep="toNextStep"
              @validationChanged="updateValidState"
          >
          </flow-step>
        </v-expansion-panel-content>
        <v-divider
            v-if="stepIndex + 1 < showedStepsCount && !showStepHeaderDividers"
            class="mx-2"
        />
      </v-expansion-panel>
      <slot name="steps-after"></slot>
      <v-btn
          class="mb-2 btn-submit white--text"
          text
          x-large
          color="accent"
          tile
          @click="submit"
          v-show="showSubmitButton"
      >
        {{ $t('Submit') }}
      </v-btn>
    </v-expansion-panels>
    <div v-if="!steps">
      <h5>Please fix you JSON scheme</h5>
    </div>
  </div>

</template>

<script>
  import FlowStep from "./FlowStep";
  import {_} from "vue-underscore";
  import {ComponentWithModelMixin, FlowGeneratorMixin, StepperValidationMixin} from "../../mixins/FlowGenerator";
  import {FlowGeneratorBus} from "../../plugins/bus";

  export default {
    name: "FlowMultiStepper",
    components: {FlowStep},
    mixins: [FlowGeneratorMixin, ComponentWithModelMixin, StepperValidationMixin],
    computed: {
      //  To have ability to show/hide steps with logic based on flow answers
      //  we are using this computed property which expand raw `schema.steps`
      //  with `show` boolean attribute, also `index` to manage other UI logic
      steps() {
        let index = this.shiftIndex - 1;
        const showStep = this.showStep;
        const getStepRef = this.getStepRef;
        // check if the schema has steps
        if (!_.isEmpty(this.schema.steps)) {
          // append `show` property to hide/show steps
          return _.mapObject(this.schema.steps, function (val, key) {
            index += 1;
            const stepRef = getStepRef(key)
            return {
              ...val,
              key,
              index: Number(index),
              show: showStep(key),
              ref: stepRef,
            }
          })
        }
        return {}
      },
      //  returns only steps that have `show` attribute equals true
      showedSteps() {
        return _.pick(this.steps, value => value.show)
      },
      showedStepsCount() {
        return _.keys(this.showedSteps).length
      },
      showedStepsArray() {
        return _.values(this.showedSteps).sort((a, b) => a.index > b.index)
      },
      firstShowedStep() {
        return _.first(this.showedStepsArray)
      },
      lastShowedStep() {
        return _.last(this.showedStepsArray)
      },
      // Our model have steps names as first-level keys,
      // but usually to implement some logic based on answers we wanna to work with
      // {[question id]: [answer value], ...}
      // and this property returns what we need.
      flattenModel() {
        return _.extend({}, ..._.values(this.model))
      },
      flowControl() {
        return this.schema.flowControl || false
      },
      switchControl() {
        return this.schema.switchControl || false
      },
      showStepHeaderDividers() {
        return this.schema.showStepHeaderDividers || false
      },
      autoContinue() {
        return this.schema.autoContinue || false
      },
      hideSubmitButton() {
        return this.schema.hideSubmitButton || false
      },
      autoSubmit() {
        return this.schema.autoSubmit || false
      },
      stepsCount() {
        return this.showedStepsCount + this.stepsBeforeCount
      },
      showSubmitButton() {
        return !this.hideSubmitButton
      },
      shiftIndex() {
        return this.stepsBeforeCount
      },
      activeStepsCount() {
        return this.activeSteps.length
      },
      lastActivated() {
        // expansion-panels v-model is pushing last opened into end of array
        // so this property is about state of what the step user've clicked last
        if (this.activeStepsCount > 0) {
          return this.openedSteps[this.activeStepsCount - 1]
        }
        return undefined
      },
      lastOpened() {
        // it returns last step index in ordering "lo-hi"
        if (_.isEmpty(this.openedSteps)) {
          return 0
        }
        return this.openedSteps[this.openedSteps.length - 1].index
      },
      disabledStep() {
        return index => {
          return index > this.lastOpened + this.shiftIndex
        }
      },
      getStepHeaderNumber() {
        return stepKey => {
          const showedSteps = _.keys(this.showedSteps)
          return _.indexOf(showedSteps, stepKey) + this.shiftIndex + 1
        }
      },
      activeSteps: {
        // As default expansion-panels v-model is working with indexes
        // of opened panels inside it.
        // Our flow configurator is a dynamic environment which lives own life
        // and some cases, such as an adding or removing steps creates
        // confusing with indexes inside a model, so we could not use
        // any logic (like validation) of child steps
        //
        // So the mutator solve that case
        set(stepIndexes) {
          // sometimes js get with error of undefined $refs.panels
          //
          const panels = this.$refs.panels;
          const mapping = panels
            ? (
              index => ({
                index: index,
                name: this.getStepKey(index),
                step: panels.items[index]
              })
            )
            : (

              index => ({ index: index, name: this.getStepKey(index) })
            )
          this.openedSteps = _.map(
            stepIndexes,
            mapping
          )
        },
        get() {
          return _.map(this.openedSteps, step => step.index)
        }
      },
    },
    props: {
      schema: {
        type: Object,
        required: true
      },
      stepsBeforeCount: {
        type: Number,
        default: 0
      }
    },
    async mounted() {
      this.loadModel()
      this.debouncedActivateNextStep = _.debounce(this.activateNextStep, 300, true)
      await this.$nextTick()
      this.activateFirstStep()
    },
    data() {
      return {
        // opened steps model to store expansion panel state
        openedSteps: [],
        model: {},
      }
    },
    watch: {
      'showedSteps': function (steps) {
        this.validationSteps = _.mapObject(
          steps,
          (val, key) => {
            if (key in this.validationSteps) {
              return this.validationSteps[key]
            }
            // if the step key is not present in this.validationSteps
            // this can be a dynamically added step, check if this
            // step has any questions or question validations
            return this.hasEmptyQuestionValidations(steps[key])
          }
        )
      }
    },
    methods: {
      // check if step questions have empty validations
      hasEmptyQuestionValidations(step) {
        if (step.questions) {
          // filter the questions that actually have validations
          const questionsWithValidation = step.questions.filter(question => {
            return !!question.validation
          })
          return questionsWithValidation.length === 0
        }
        // no questions, so that's empty validation too
        return true
      },
      getNextOpenedInvalidStep() {
        return _.find(
          this.openedSteps,
          step => this.validationSteps[step.name] === false
        )
      },
      // action to activate next flow step
      getNextStepIndex() {
        const showedStepsIndexes = _.map(this.showedStepsArray, step => step.index)
        // if no any opened before - use first to activate
        const firstStepIndex = this.firstShowedStep.index
        if (this.lastOpened === 0 && !_.contains(showedStepsIndexes, firstStepIndex)) {
          return showedStepsIndexes[0]
        }
        // go to next opened step if has some of them are not valid
        const openedInvalid = this.getNextOpenedInvalidStep()
        if (openedInvalid) return openedInvalid.index

        // if not found lastOpenedIndex - return undefined
        const currentIndexOfStep = _.findIndex(
          showedStepsIndexes, el => el === this.lastOpened
        )
        if (currentIndexOfStep === -1) return

        // if nextIndex is out of array length - return undefined
        const candidatesToOpen = _.difference(showedStepsIndexes, this.activeSteps)
        const nextPossibleStepIndex = candidatesToOpen ? candidatesToOpen[0] : showedStepsIndexes.last()

        // sometimes when some steps were added or removed
        // we could have a step.index bigger than length of array
        const biggestIndex = _.max(showedStepsIndexes)
        if (nextPossibleStepIndex <= biggestIndex) {
          return nextPossibleStepIndex
        } else {
          return biggestIndex
        }
      },
      async toNextStep(stepFrom) {
        // prevent from scrolling to itself
        if (stepFrom === this.lastShowedStep.key) return
        // if stepFrom is undefined it means we call it from outside
        // so we apply it as valid
        if (!stepFrom) {
          this.debouncedActivateNextStep()
          return
        }
        const isCurrentStepValid = this.validationSteps[stepFrom];
        if (isCurrentStepValid) {
          const stepIndex = this.steps[stepFrom].index
          if (this.isLastStep(stepIndex) && this.autoSubmit) {
            this.submit()
          } else if (stepIndex > 0 && stepIndex > this.lastOpened + 1) {
            // do not open steps that are more than 1 step after
            // the last opened step, only the first is allowed to open
            return
          } else {
            this.debouncedActivateNextStep()
          }
        }
      },
      activateNextStep() {
        const nextStepIndex = this.getNextStepIndex()
        if (!nextStepIndex) {
          console.error('Next step index not found')
          return
        }
        // open all steps until and including the next step index
        let stepKey = this.getStepKey(nextStepIndex)
        this.activateStep(stepKey, true)
      },
      activateStep(stepKey, scrollTo = false) {
        const step = this.getStepRef(stepKey)
        if (step) {
          // get expansion panel and click to open it
          this.openStep(step, stepKey, scrollTo)
        }
      },
      deactivateStep(stepKey) {
        const step = this.getStepRef(stepKey)
        if (step) {
          // get expansion panel and click to open it
          this.closeStep(step, stepKey)
        }
      },
      activateFirstStep() {
        const showedSteps = _.values(this.showedSteps)
        const showedStepsIndexes = _.map(showedSteps, step => step.index)
        const stepKey = this.getStepKey(showedStepsIndexes[0])
        this.activateStep(stepKey, true)
      },
      getStepPanelHeader(step) {
        const panel = step.$parent.$parent.$children
        const panelHeaderIndex = panel.findIndex(
          val => val.$options._componentTag === 'v-expansion-panel-header'
        )
        // it should find a header of step, but it always safe to check
        if (panelHeaderIndex !== -1) {
          return panel[panelHeaderIndex]
        } else {
          console.error("panel not found, panelHeaderIndex=", panelHeaderIndex, " panel=", panel)
          return null
        }
      },
      openStep(step, stepKey, scrollTo = false) {
        const panelHeader = this.getStepPanelHeader(step)
        if (!panelHeader) return
          // do not click steps that are already active, because this will close them
        if (!panelHeader.$el.classList.contains('v-expansion-panel-header--active')) {
          panelHeader.$el.click()
        }
        // only scroll to the panel header if it is visible (clientWidth > 0)
        if (scrollTo && panelHeader.$el.clientWidth > 0) {
          // also do not scroll when the step is already in opened steps
          // the page should not jump down each time you edit previous data
          for (const openedStep of this.openedSteps) {
            if (openedStep.name === stepKey) {
              return null
            }
          }
          setTimeout(() => {
            this.goTo(step, stepKey)
          }, 200)
        }
      },
      closeStep(step, stepKey) {
        const panelHeader = this.getStepPanelHeader(step)
        if (!panelHeader) return
          // do not click steps that are already active, because this will close them
        if (panelHeader.$el.classList.contains('v-expansion-panel-header--active')) {
          panelHeader.$el.click()
        }
      },
      async goTo(step, stepKey) {
        if (step) {
          await this.$vuetify.goTo(step, {
            duration: 400,
            offset: this.schema.stepScrollOffset || 90,
            easing: 'easeInOutCubic'
          })
          // when a new step has opened -
          // check it might be valid with predefined values
          // so we can go to the next one, of course, when that is not a last
          const currentValid = await this.validateStep(stepKey, true)
          const isLastStep = this.lastShowedStep.key === stepKey
          if (currentValid && !isLastStep) {
            await this.$nextTick(() => {
              this.debouncedActivateNextStep()
            })
          }
        }
      },
      closeAllSteps() {
        this.openedSteps = []
      },
      openAllSteps() {
        this.activeSteps = _.map(this.steps, (val) => val.index)
      },
      showStep(stepName) {
        // return boolean status about
        // is show should be showed on UI
        const hide = this.schema.steps[stepName].hide
        const when = this.schema.steps[stepName].when
        return !hide && (!when || this.isMatchWhen(this.flattenModel, when))
      },
      async validateAll() {
        for (const stepIndex in this.showedSteps) {
          await this.validateStep(this.showedSteps[stepIndex].key)
        }
      },
      async validateStep(stepKey, silent = false) {
        // run validation on currently activated expansion-panel's child form
        const stepRef = this.getStepRef(stepKey)
        if (!stepRef) return undefined;
        const isValid = await stepRef.validate(silent);
        return isValid
      },
      refTemplate(ref) {
        return `step-${ref}`
      },
      getStepKey(index) {
        // get current step by index
        // and return Ref to flowStepper component
        const step = _.pairs(
          _.pick(this.showedSteps, step => step.index === index)
        )
        // sa pairs return us ["key", "value"] when transform object
        if (step.length > 0) return step[0][0]
      },
      getStepRefByIndex(index) {
        // return reference to step component by index
        if (index >= this.stepsCount) return
        const stepKey = this.getStepKey(index)
        return this.getStepRef(stepKey)
      },
      getStepRef(stepKey) {
        // return reference to step component by stepKey
        return this.getRef(
          this.refTemplate(stepKey)
        )
      },
      getRef(ref) {
        return this.$refs[ref] && this.$refs[ref][0];
      },
      removeStep(stepKey) {
        FlowGeneratorBus.$emit('remove-step', stepKey)
      },
      isLastStep(index) {
        return this.stepsCount === index + 1
      },
      resetState() {
        this.closeAllSteps()
        setTimeout(this.activateFirstStep, 1000)
      }
    }
  }
</script>

<style lang="scss">
  .v-stepper__content.flow-stepper-content {
    .v-stepper__wrapper {
      height: auto !important;
    }
  }
</style>
